import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, inject, type OnDestroy, type OnInit, Renderer2 } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { ActivatedRoute, NavigationCancel, NavigationEnd, NavigationStart, Router, RouterOutlet } from '@angular/router';
import { type Observable, Subscription } from 'rxjs';
import { filter, map, mergeMap } from 'rxjs/operators';
import { AppThemeConfig } from './models/app-theme-config.model';
import { SpinnerEvent } from './models/spinner.model';
import { ConfigResourceService } from './services/config-resource.service';
import { FaviconService } from './services/favicon.service';
import { BreakPoint, ResponsiveLayoutService } from './services/responsive-layout.service';
import { SpinnerService } from './services/spinner.service';
import { AppThemeService } from './shared/color-theme/app-theme.service';
import { FooterComponent } from './shared/footer/footer.component';
import { PageSpinnerComponent } from './shared/page-spinner/page-spinner.component';
import { PrescriptionContainerComponent } from './shared/prescription-container/prescription-container.component';
import { PrescriptionsHeaderComponent } from './shared/prescriptions-header/prescriptions-header.component';
import { UniversalNavComponent } from './shared/universal-nav/universal-nav.component';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    CommonModule,
    RouterOutlet,
    UniversalNavComponent,
    FooterComponent,
    PrescriptionContainerComponent,
    PrescriptionsHeaderComponent,
    PageSpinnerComponent
  ]
})
export class AppComponent implements OnInit, OnDestroy {
  private readonly router = inject(Router);
  private readonly activatedRoute = inject(ActivatedRoute);
  private readonly configService = inject(ConfigResourceService);
  private readonly titleService = inject(Title);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly spinnerService = inject(SpinnerService);
  private readonly renderer = inject(Renderer2);
  private readonly applicationTheme = inject(AppThemeService);
  private readonly responsiveLayoutService = inject(ResponsiveLayoutService);
  private readonly faviconService = inject(FaviconService);

  readonly loaderIcon: string = 'ball-spin-clockwise';
  title: string = 'NextGen Drug Compare';
  showNavigation: boolean = true;
  showFooter: boolean = true;
  showLoaderMessage: boolean = false;
  loaderText: string = 'Loading...';
  private readonly subscriptions = new Subscription();

  constructor() {
    setTimeout(() => {
      this.renderer.selectRootElement('#initial-la-ball-spin-clockwise')?.remove();
    });
  }

  ngOnInit(): void {
    this.subscriptions.add(
      this.responsiveLayoutService.isBreakPoint$(BreakPoint.COMPACT).subscribe({
        next: isMobile => {
          this.applicationTheme.initCreateAppTheme(this.renderer, isMobile);
        }
      })
    );

    this.subscriptions.add(
      this.configService.configLoaded$.subscribe({
        next: loaded => {
          if (loaded) {
            this.showFooter = this.configService.getProcessedResourceValue<boolean>('Spa_Site_Show_Footer', true);
            this.showLoaderMessage = this.configService.getProcessedResourceValue<boolean>('Spa_Site_Loader_Message_Enabled', true);
            this.updateCSSVariables();
            const contentServerUrl = this.configService.getProcessedResourceValue<string>(
              'ContentServerUrl',
              'https://contentserver.destinationrx.com/ContentServer/DRxProductContent'
            );
            const faviconImage = this.configService.getProcessedResourceValue<string>('SiteConfig_Favicon', '');
            this.updateFavicon(this.renderer, contentServerUrl, faviconImage);
            this.cdr.markForCheck();
          }
        }
      })
    );

    this.subscriptions.add(
      this.router.events.pipe(filter(event => event instanceof NavigationStart)).subscribe({
        next: event => {
          if (this.showLoaderMessage) {
            this.loaderText = this.spinnerService.getLoaderMessage(event.url.split('?')?.[0] ?? '');
            this.cdr.markForCheck();
          }
          this.spinnerService.show({ event: SpinnerEvent.NAVIGATION, ...(this.loaderText && { loaderText: this.loaderText }) });
        }
      })
    );

    const routerNavigationEndEvent$: Observable<NavigationEnd> = this.router.events.pipe(filter(event => event instanceof NavigationEnd));

    this.subscriptions.add(
      routerNavigationEndEvent$.subscribe({
        next: (event: NavigationEnd) => {
          this.showNavigation = !event.url.includes('/Resources/');
          this.spinnerService.hide({ event: SpinnerEvent.NAVIGATION });
          this.loaderText = '';
          this.cdr.markForCheck();
        }
      })
    );

    // listening for when we navigate to a new page so we can update the document title
    this.subscriptions.add(
      routerNavigationEndEvent$
        .pipe(
          map(() => {
            let selectedRoute: ActivatedRoute = this.activatedRoute;
            while (selectedRoute.firstChild) {
              selectedRoute = selectedRoute.firstChild;
            }
            return selectedRoute;
          }),
          filter(route => route.outlet === 'primary'),
          mergeMap(route => route.data)
        )
        .subscribe({
          next: event => {
            const rawTitle: string = event['title'];
            const processedEventTitle: string | null | undefined = this.configService.processValueProviders<string>(rawTitle);
            this.titleService.setTitle(processedEventTitle || rawTitle);
          }
        })
    );

    this.subscriptions.add(
      this.router.events.pipe(filter(event => event instanceof NavigationCancel)).subscribe({
        next: () => {
          this.spinnerService.hide({ event: SpinnerEvent.NAVIGATION });
          this.loaderText = '';
          this.cdr.markForCheck();
        }
      })
    );

    this.subscriptions.add(
      this.spinnerService
        .getLoaderText$()
        .pipe(filter(loaderText => loaderText !== this.loaderText))
        .subscribe({
          next: loaderText => {
            this.loaderText = loaderText;
            this.cdr.markForCheck();
          }
        })
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  private updateFavicon(renderer: Renderer2, contentServerUrl: string, faviconImage: string): void {
    this.faviconService.setFavicon(renderer, contentServerUrl, faviconImage);
  }

  private updateCSSVariables(): void {
    const mapPinColor: string = this.configService.getProcessedResourceValue<string>('DC5_Pharmacy_Color', '901C00');

    AppThemeConfig.appendStylesToDocument(this.renderer, '--mdc-color-', [
      { name: 'map-pin', value: `#${mapPinColor}` }
    ]);
  }
}
